import useSWR from "swr";

import { ContactSortField, SortDirection } from "@/components/screens/Contacts/types";
import SWR_KEYS from "@/constants/swr-keys";
import { sortContactItems } from "@/helpers/contact-sort.helpers";
import { normalizeSingleFilterFormData, sanitizeFilters } from "@/helpers/request-helpers";
import IContact from "@/interfaces/contact.interface";
import contactService from "@/services/contact.service";
import { IFilter } from "@/types/filter.types";
import {
  GetRequestProps,
  PaginatedResponse,
  RequestPagination,
  RequestsBaseReturn,
} from "@/types/requests.types";

export type UseGetContactsReturn = RequestsBaseReturn<PaginatedResponse<IContact> | null>;

export interface UseGetContactsProps extends RequestPagination, GetRequestProps {
  filters: IFilter<IContact>[];
  sortField?: ContactSortField | null;
  sortDirection?: SortDirection | null;
}

type GetAllSwrParams = [
  string,
  number,
  number,
  IFilter<IContact>[],
  string | null,
  string | null,
];

const useGetContacts = ({
  pageNumber,
  pageSize,
  filters,
  sortField = null,
  sortDirection = null,
  isPaused = false,
}: UseGetContactsProps): UseGetContactsReturn => {
  const normalizedFilter = sanitizeFilters(
    normalizeSingleFilterFormData(filters, "firstName")
  );
  const sortBy = sortField ?? null;
  const sortDir = sortDirection ?? null;

  const {
    data = null,
    isValidating: isLoading,
    error,
  } = useSWR<PaginatedResponse<IContact>, Error, GetAllSwrParams>(
    [SWR_KEYS.getContacts, pageNumber, pageSize, normalizedFilter, sortBy, sortDir],
    async ([, _pageNumber, _pageSize, _filters, _sortBy, _sortDir]) => {
      const response = await contactService.filter(
        _filters,
        {
          pageNumber: _pageNumber,
          pageSize: _pageSize,
        },
        _sortBy,
        _sortDir
      );

      if (!_sortBy || !_sortDir || !response?.items?.length) {
        return response;
      }

      return {
        ...response,
        items: sortContactItems(
          response.items,
          _sortBy as ContactSortField,
          _sortDir as SortDirection
        ),
      };
    },
    {
      revalidateOnFocus: false,
      isPaused: () => isPaused,
    }
  );

  return { data, isLoading, error };
};

export default useGetContacts;
